import { BaseStore } from "./base_store";
import { runInAction, makeAutoObservable } from "mobx"
// import UserAPI from "../api/user";
import IdentityAPI from "../api/identity_api";
import { api_get, api_post } from "api/utils"

export type IdentityProps = {
    uuid: string;
	key: string;
	name: string;
    email: string;
    handle: string;
    phone: string;
    addr1: string;
    addr2: string;
    city: string;
    state: string;
    zipcode: string;
	color: string;
	role: string;
    flags: string[];
    presence: string;
    verified: string;
    dedicated: boolean;
    team: string;
	date_created: string;
	date_updated: string;

    // user stuff
    tasks_remaining: number;
    current_credit: number; // how much is remaining
    credit_included: number; // how much was included?
    next_billing_cycle_date: string;
    subscription_service: string;
    subscription_product: string;

	updateProps?: (attributes: IdentityProps) => void;
};

// Domain object Identity
export class Identity implements IdentityProps {
    uuid = "" ;
	key = "";
    name = "";
    handle = "";
    email = "";
    phone = "";
    addr1 = "";
    addr2 = "";
    city = "";
    state = "";
    zipcode = "";
	color = "";
	role  = "";
    flags = [] as string[];
    presence = "";
    verified = "";
    dedicated = false;
    team = "";

    // user stuff
    tasks_remaining = 0;
    current_credit = 0;
    credit_included = 0;
    next_billing_cycle_date = "";
    subscription_service = "";
    subscription_product = "";

	date_created = "";
	date_updated = "";


	constructor(attributes: IdentityProps) {
		makeAutoObservable(this, {
			updateProps: false
		})
		if(attributes) {
			this.updateProps(attributes);
        }
	}

	updateProps(attributes: IdentityProps) {
        runInAction(() => {
            Object.assign(this, attributes);
        });
	}
}


export class IdentityStore extends BaseStore<IdentityProps, Identity> {

    FETCHED_IDENTITIES = [] as string[];
    _fetch_queue = [] as string[];
    _fetch_queue_timer = 0;
    _fetchIdentity = '';

    getIdentity(key: string | undefined) {
		if(!key) {return null;}
        // console.log("we have %d items during getIdentity", this.items.length);
        if (!this.items)  {return null; }
        // Match by key or uuid to handle both urlsafe keys and uuid identifiers
        const ret = this.items.find((c) => c.key === key || c.uuid === key);
        // console.log("Returning %o, %o", ret?.key, ret);
        return ret as Identity;
    }

	getIdentities() {
		return this.items;
	}

	async fetchIdentities(cursor = "") {
        // Get the identities owned by fetchIdentities.
		IdentityAPI.list(cursor).then((resp) => {
			this.receiveUpdate(resp.items);
			if(resp.cursor) {
				this.fetchIdentities(resp.cursor);
			}
            return resp.items;
		})
	}

	async fetchAllIdentities(identity: string, cursor = "") {
		return IdentityAPI.listAll(identity, cursor).then((resp) => {
			this.receiveUpdate(resp.items);
			if(resp.cursor) {
				this.fetchAllIdentities(identity, resp.cursor);
			}
            //  return resp.items;
		})
	}

    async fetchListByTeam(workKey: string, team: string, cursor=''): Promise<IdentityProps[]>  {
        const url = '/identity/list/by-team';
        const data = { identity: workKey, team, cursor };
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListByTeam(workKey, team, data.cursor);
            }
            return data.items;
        }
        return [];
    };

    async fetchListByDedicated(workKey: string, cursor=''): Promise<IdentityProps[]>  {
        const url = '/identity/list/by-dedicated';
        const data = { identity: workKey, cursor };
        const response = await api_get(url, data);
        if (response.ok) {
            const data = await response.json();
            this.receiveUpdate(data.items);
            if(data?.cursor) {
                this.fetchListByDedicated(workKey, data.cursor);
            }
            return data.items;
        }
        return [];
    };

    _add_to_fetch_queue(key: string) {
        if(this._fetch_queue.includes(key))
            return;
        this._fetch_queue.push(key);
    }

    async doFetchQueue() {
        const items = this._fetch_queue;
        this._fetch_queue = [];
        if(items.length === 0)
            return;

        IdentityAPI.getByKeyList(this._fetchIdentity, items)
            .then((data: { items: IdentityProps[] }) => {
                this.receiveUpdate(data.items);
                if (this._fetch_queue.length > 0) {
                    this._fetch_queue_timer = window.setTimeout(() => {
                        this.doFetchQueue();
                    }, 500);
                }
                else {
                    this._fetch_queue_timer = 0;
                }
            })
            .catch((err) => {
                console.error("error fetching list: %o", err);
            });
    }

    fetchIdentity(identity: string, key: string) {
        this._fetchIdentity = identity;
        if(this.FETCHED_IDENTITIES.includes(key))
            return null;
        this.FETCHED_IDENTITIES.push(key);
        this._add_to_fetch_queue(key);

        if(!this._fetch_queue_timer)  {
            this._fetch_queue_timer = window.setTimeout(() => {
                this.doFetchQueue();
            }, 100);
        }
    }

    async sudoSetVerified(key: string, verified: string) : Promise<Identity> {
        return IdentityAPI.sudoSetVerified(key, verified).then((data: Identity) => {
            this.receiveUpdate(data as IdentityProps);
            return data;
        });
    }

    async sudoSetDedicated(key: string, dedicated: string) : Promise<Identity>{
        return IdentityAPI.sudoSetDedicated(key, dedicated).then((data: Identity) => {
            this.receiveUpdate(data as IdentityProps);
            return data;
        });
    }

    async sudoSetOnCallSupport(workKey: string, uuid: string) : Promise<Identity>{
        const data = {identity: workKey, support_identity: uuid};
        return api_post('/identity/sudo-set-on-call-support', data)
            .then(async (res) => {
            const data = await res.json();
            return data?.uuid || null;
        }).catch((err) => {
            console.error("sudoSetOnCallSupport failed: %o", err);
            return null;
        });
    }

    async supportFetchIdentityForUser(userKey: string) {
        return IdentityAPI.supportFetchIdentityForUser(userKey).then((data) => {
            this.receiveUpdate(data);
            return data;
        });
    }

    async supportGetUserUuid(identityKey: string) : Promise<string | null>{
        return api_get(`/identity/support-get-user-uuid?search_identity=${identityKey}`)
            .then(async (res) => {
            const data = await res.json();
            return data?.uuid || null;
        }).catch((err) => {
            console.error("supportGetUserUuid failed: %o", err);
            return null;
        });
    }


    async managerCreateIdentity(workKey: string, user_uuid: string, name: string): Promise<IdentityProps | null> {
        const data = { identity: workKey, user_uuid, name };
        return api_post("/identity/manager-create", data)
            .then(async (res) => {
                const data = await res.json();
                return data as IdentityProps;
            }).catch((err) => {
                console.error("managerCreateIdentity failed: %o", err);
                return null;
            });
    }


	modelConstructor() {
		return Identity;
	}

}
